import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import { Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { FormEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import InputPassword from '@/components/InputPassword';
import PhoneTextField from '@/components/common/PhoneTextField';
import { authApi } from './auth-api';
import { mapAuthError } from './auth-error-map';

// ─── Component ────────────────────────────────────────────────────────────────

export default function AuthRegisterCredentials({
    email,
    redirectPath,
    oauthQuery,
    onBack,
    onSuccess,
    lockEmail = false,
}: {
    email: string;
    redirectPath: string;
    oauthQuery?: string;
    onBack: () => void;
    onSuccess: () => void;
    lockEmail?: boolean;
}) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setPasswordError(null);
        setLoading(true);
        try {
            await authApi.signUpEmail({
                name,
                email,
                password,
                // Invited team members don't provide a phone (field hidden); omit it entirely.
                phoneNumber: lockEmail ? undefined : phone,
                callbackURL: redirectPath,
                oauthQuery,
            });
            onSuccess();
        } catch (error) {
            const mapped = mapAuthError(error, t, 'register');
            if (mapped.field === 'password') {
                setPasswordError(mapped.message);
            } else {
                enqueueSnackbar(mapped.message, { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack spacing={3}>
            {/* Back button */}
            <Stack direction="row" alignItems="center" spacing={1}>
                {!lockEmail && (
                    <Tooltip title={t('auth.register.back')}>
                        <IconButton onClick={onBack} color="primary" sx={{ ml: -0.5 }}>
                            <ArrowBackRounded color="primary" />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Email chip */}
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                        {t('auth.step.email.signing-in-as')}
                    </Typography>
                    <Chip
                        label={email}
                        onClick={lockEmail ? undefined : onBack}
                        variant="outlined"
                        size="small"
                        sx={{
                            fontWeight: 500,
                            cursor: lockEmail ? 'default' : 'pointer',
                            borderRadius: 'var(--mui-shape-sm)',
                        }}
                    />
                </Box>
            </Stack>

            {/* Form */}
            <Stack component="form" spacing={2} onSubmit={onSubmit}>
                <TextField
                    label={t('auth.register.name-label')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoFocus
                    autoComplete="name"
                    fullWidth
                />

                <InputPassword
                    label={t('str.password')}
                    value={password}
                    onChange={(e) => {
                        setPassword((e as React.ChangeEvent<HTMLInputElement>).target.value);
                        if (passwordError) {
                            setPasswordError(null);
                        }
                    }}
                    name="password"
                    required
                    error={!!passwordError}
                    helperText={passwordError || undefined}
                />

                {/* Phone is required for a normal sign-up but NOT asked of invited team members
                    (they arrive with a locked email); historically invites never collected it. */}
                {!lockEmail && (
                    /* PhoneTextField redefines onChange to (value: string) — cast required due to TextFieldProps overlap */
                    <PhoneTextField
                        value={phone}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onChange={setPhone as any}
                        required
                        helperText={t('auth.register.phone-helper')}
                    />
                )}

                <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{ py: 1.5, fontWeight: 700 }}
                >
                    {loading ? t('auth.common.loading') : t('auth.register.cta')}
                </Button>
            </Stack>

            {/* Terms */}
            <Typography variant="caption" color="text.secondary" textAlign="center">
                <Trans
                    i18nKey="auth.register.terms"
                    components={{
                        terms: (
                            <Box
                                component="a"
                                href="/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: 'primary.main', textDecoration: 'none' }}
                            />
                        ),
                        privacy: (
                            <Box
                                component="a"
                                href="/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: 'primary.main', textDecoration: 'none' }}
                            />
                        ),
                    }}
                />
            </Typography>
        </Stack>
    );
}
